import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type CSSProperties
} from 'react';
import { flushSync } from 'react-dom';
import { Markdown } from './Markdown';
import { Toast } from './Toast';
import type { WysiwygSaveState } from './saveState';
import type { FormattingAction } from './FormattingToolbar';
import {
  applyFormattingAction,
  detectActiveFormats,
  handleListEnter,
  type ActiveFormats
} from '../../lib/markdownFormatting';
import { EditorMenuPopover } from './EditorMenuPopover';
import { useTextareaAutoGrow } from '../../hooks/useTextareaAutoGrow';

export type MarkdownEditorMode = 'edit' | 'preview';

export interface MarkdownEditorProps {
  /** Markdown source string. Controlled. */
  value: string;
  /** Fires on every input with the new source. */
  onChange: (md: string) => void;
  /** Fires on Cmd/Ctrl+S inside the textarea. */
  onSave: () => void;
  /** Save state (Saving/Saved/Conflict/Failed/Idle) — surfaces conflict banner. */
  saveState: WysiwygSaveState;
  /** Active surface — Edit (textarea source) or Preview (rendered HTML). */
  mode: MarkdownEditorMode;
  /** Textarea placeholder shown when `value` is empty. */
  placeholder?: string;
  /** When true the textarea is read-only (preview-pinned). */
  readonly?: boolean;
  /** Optional className applied to the outer wrapper. */
  className?: string;
  /** Optional outer style hook for the wrapper. */
  style?: CSSProperties;
  /**
   * Test prefix. The wrapper gets `${dataTestId}`; the textarea gets
   * `${dataTestId}-content`; the conflict banner gets
   * `${dataTestId}-conflict-banner`; slash menu gets `${dataTestId}-slash-menu`.
   */
  dataTestId?: string;
  /** Fires when caret/selection changes with formats active at that range. */
  onActiveFormatsChange?: (active: ActiveFormats) => void;
}

export interface MarkdownEditorRef {
  /** Splice markdown syntax for the given action around the current selection. */
  insertSyntax: (action: FormattingAction) => void;
  /** Programmatically focus the textarea. */
  focus: () => void;
  /** Formats active at the current textarea selection. */
  getActiveFormats: () => ActiveFormats;
}

interface MenuState {
  /** Index in `value` where the `/` trigger lives. */
  triggerIndex: number;
  /** Highlighted item in the (filtered) list. */
  index: number;
  /** Viewport coords for the popover anchor. */
  x: number;
  y: number;
  /** Running query, derived from value[triggerIndex+1..caret]. */
  query: string;
}

interface SlashItem {
  id: string;
  label: string;
}

const SLASH_ITEMS: SlashItem[] = [
  { id: 'h1', label: 'Heading 1' },
  { id: 'h2', label: 'Heading 2' },
  { id: 'h3', label: 'Heading 3' },
  { id: 'bullet', label: 'Bullet list' },
  { id: 'ordered', label: 'Ordered list' },
  { id: 'code', label: 'Code block' },
  { id: 'quote', label: 'Quote' },
  { id: 'divider', label: 'Divider' },
  { id: 'link', label: 'Link' }
];

const CONFLICT_COPY =
  'Someone else saved since you opened this. Reload to see their changes — your unsaved text is preserved below.';

function filterItems<T extends { label: string }>(items: T[], q: string): T[] {
  if (!q) return items;
  const needle = q.toLowerCase();
  return items.filter((i) => i.label.toLowerCase().includes(needle));
}

/**
 * Notion-style markdown editor primitive. Two surfaces controlled by the
 * `mode` prop:
 *
 *   - `'edit'`     — a `<textarea>` showing markdown source. Auto-grows via
 *                    `field-sizing: content` (Chromium); a JS `input` handler
 *                    sets `style.height = scrollHeight + 'px'` as a fallback
 *                    for Firefox / WebKit. Cmd/Ctrl+S fires `onSave`. Slash
 *                    menu (`/` at the start of an empty line) drives markdown
 *                    insertion at the caret.
 *   - `'preview'`  — the same markdown rendered read-only via the existing
 *                    `<Markdown>` primitive. Mode swap is non-destructive:
 *                    toggling back to Edit restores the source verbatim.
 *
 * The conflict banner renders above the active surface whenever
 * `saveState === 'conflict'`. The failed-save toast renders bottom-right
 * (same UX as `WysiwygEditor`) for `saveState === 'failed'`.
 *
 * The imperative ref exposes `insertSyntax(action)` so a sibling
 * `<FormattingToolbar>` can request a splice without holding the textarea
 * ref itself. CSS namespace `pn-markdown-editor` (-textarea / -preview /
 * -conflict / -menu / -menu-item).
 */
export const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>(
  function MarkdownEditor(
    {
      value,
      onChange,
      onSave,
      saveState,
      mode,
      placeholder = 'Start writing — type / for commands…',
      readonly = false,
      className,
      style,
      dataTestId,
      onActiveFormatsChange
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [menu, setMenu] = useState<MenuState | null>(null);
    const [toastDismissed, setToastDismissed] = useState(false);
    const menuRef = useRef<MenuState | null>(null);
    menuRef.current = menu;

    const tid = (suffix: string) =>
      dataTestId ? `${dataTestId}-${suffix}` : undefined;

    const filteredSlash = useMemo(() => {
      if (!menu) return SLASH_ITEMS;
      return filterItems(SLASH_ITEMS, menu.query);
    }, [menu]);

    const filteredSlashRef = useRef(filteredSlash);
    filteredSlashRef.current = filteredSlash;

    useTextareaAutoGrow(textareaRef, value, mode === 'edit');

    // ------------------------------------------------------------ failure toast
    useEffect(() => {
      if (saveState === 'failed') setToastDismissed(false);
    }, [saveState]);

    // ------------------------------------------------------------ splice helpers
    const splice = useCallback(
      (
        replaceStart: number,
        replaceEnd: number,
        inserted: string,
        selectionStartInInserted: number,
        selectionEndInInserted?: number
      ) => {
        const ta = textareaRef.current;
        if (!ta) return;
        const next =
          value.slice(0, replaceStart) + inserted + value.slice(replaceEnd);
        const absStart = replaceStart + selectionStartInInserted;
        const absEnd =
          replaceStart + (selectionEndInInserted ?? selectionStartInInserted);
        // Force a synchronous commit so the textarea's DOM value reflects
        // `next` before we set the selection range below. Without this the
        // selection has to be scheduled via rAF, which races with anything
        // that runs after the user's click (e.g. a Playwright fill() on a
        // sibling input). Production flows already keep focus on the
        // textarea via onMouseDown.preventDefault on toolbar/menu items, so
        // we don't need to call .focus() here.
        flushSync(() => {
          onChange(next);
        });
        if (document.activeElement === ta) {
          ta.setSelectionRange(absStart, absEnd);
        }
        onActiveFormatsChange?.(detectActiveFormats(next, absStart, absEnd));
      },
      [value, onChange, onActiveFormatsChange]
    );

    const reportActiveFormats = useCallback(() => {
      const ta = textareaRef.current;
      if (!ta || mode !== 'edit') return;
      onActiveFormatsChange?.(
        detectActiveFormats(value, ta.selectionStart, ta.selectionEnd)
      );
    }, [mode, onActiveFormatsChange, value]);

    const runSyntax = useCallback(
      (action: FormattingAction) => {
        if (mode !== 'edit') return;
        const ta = textareaRef.current;
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const result = applyFormattingAction(value, start, end, action);
        const insertedLen = result.value.length - value.length + (end - start);
        const inserted = result.value.slice(start, start + insertedLen);
        splice(
          start,
          end,
          inserted,
          result.selectionStart - start,
          result.selectionEnd - start
        );
      },
      [mode, splice, value]
    );

    useImperativeHandle(
      ref,
      () => ({
        insertSyntax: runSyntax,
        focus: () => textareaRef.current?.focus(),
        getActiveFormats: () => {
          const ta = textareaRef.current;
          if (!ta) return {};
          return detectActiveFormats(
            value,
            ta.selectionStart,
            ta.selectionEnd
          );
        }
      }),
      [runSyntax, value]
    );

    // ------------------------------------------------------------ menu state
    const openSlashMenuAt = useCallback((triggerIndex: number) => {
      const ta = textareaRef.current;
      if (!ta) return;
      const rect = ta.getBoundingClientRect();
      setMenu({
        triggerIndex,
        index: 0,
        x: rect.left + 12,
        y: rect.top + 28,
        query: ''
      });
    }, []);

    const closeMenu = useCallback(() => setMenu(null), []);

    const selectMenuItem = useCallback(
      (idx: number) => {
        const m = menuRef.current;
        const ta = textareaRef.current;
        if (!m || !ta) return;
        if (filteredSlashRef.current.length === 0) return;
        const replaceStart = m.triggerIndex;
        const replaceEnd = ta.selectionStart;
        const item = filteredSlashRef.current[idx];
        if (!item) return;
        const slashInserts: Record<string, string> = {
          h1: '# ',
          h2: '## ',
          h3: '### ',
          bullet: '- ',
          ordered: '1. ',
          code: '```\n\n```',
          quote: '> ',
          divider: '\n---\n',
          link: '[text](https://)'
        };
        const ins = slashInserts[item.id];
        if (ins) {
          const caret =
            item.id === 'code'
              ? '```\n'.length
              : item.id === 'link'
                ? '[text'.length
                : ins.length;
          splice(replaceStart, replaceEnd, ins, caret);
        }
        closeMenu();
      },
      [closeMenu, splice]
    );

    // ------------------------------------------------------------ key handlers
    const onTextareaKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        const ta = e.currentTarget;
        const m = menuRef.current;

        // Cmd/Ctrl+S — save. Suppress browser default. Stop propagation so a
        // page-level keydown handler doesn't fire twice.
        if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) {
          e.preventDefault();
          e.stopPropagation();
          onSave();
          return;
        }

        // Menu open: ArrowDown / ArrowUp / Enter / Escape are owned by the menu.
        if (m) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const len = filteredSlashRef.current.length || 1;
            setMenu((prev) =>
              prev ? { ...prev, index: (prev.index + 1) % len } : prev
            );
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            const len = filteredSlashRef.current.length || 1;
            setMenu((prev) =>
              prev ? { ...prev, index: (prev.index - 1 + len) % len } : prev
            );
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            selectMenuItem(m.index);
            return;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            closeMenu();
            return;
          }
          // Other keys fall through to native typing; the input handler will
          // refresh the running query.
        }

        if (e.key === 'Enter' && !e.shiftKey) {
          const r = handleListEnter(value, ta.selectionStart, ta.selectionEnd);
          if (r.handled) {
            e.preventDefault();
            onChange(r.value);
            requestAnimationFrame(() => {
              ta.focus();
              ta.setSelectionRange(r.selectionStart, r.selectionEnd);
              onActiveFormatsChange?.(
                detectActiveFormats(r.value, r.selectionStart, r.selectionEnd)
              );
            });
            return;
          }
        }

        // Open menus on trigger keys. Capture the caret position
        // synchronously — the trigger char hasn't been inserted yet, so
        // ta.selectionStart IS the position it will land at.
        if (e.key === '/') {
          const pos = ta.selectionStart;
          const lineStart = value.lastIndexOf('\n', pos - 1) + 1;
          const line = value.slice(lineStart, pos);
          if (line === '') openSlashMenuAt(pos);
        }
      },
      [closeMenu, onSave, openSlashMenuAt, selectMenuItem, value, onChange, onActiveFormatsChange]
    );

    // ------------------------------------------------------------ refresh query
    // After every value change, re-derive the query (the chars after the
    // trigger) and close if the caret moved before the trigger.
    const onTextareaInput = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const ta = e.currentTarget;
        const nextValue = ta.value;
        onChange(nextValue);
        const m = menuRef.current;
        if (m) {
          const caret = ta.selectionStart;
          if (caret <= m.triggerIndex || m.triggerIndex >= nextValue.length) {
            closeMenu();
          } else {
            const next = nextValue.slice(m.triggerIndex + 1, caret);
            setMenu({ ...m, query: next, index: 0 });
          }
        }
      },
      [closeMenu, onChange]
    );

    // Close the menu when the user clicks outside.
    useEffect(() => {
      if (!menu) return;
      function onDocClick(e: MouseEvent) {
        const tgt = e.target as HTMLElement | null;
        if (!tgt) return;
        if (tgt.closest('[data-menu-pop]')) return;
        if (tgt === textareaRef.current) return;
        closeMenu();
      }
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [menu, closeMenu]);

    const showToast = saveState === 'failed' && !toastDismissed;
    const wrapperCls = ['pn-markdown-editor', className]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={wrapperCls}
        data-mode={mode}
        data-testid={dataTestId}
        style={style}
      >
        {saveState === 'conflict' ? (
          <div
            className="pn-markdown-editor-conflict"
            role="alert"
            data-testid={tid('conflict-banner')}
          >
            {CONFLICT_COPY}
          </div>
        ) : null}

        {mode === 'edit' ? (
          <textarea
            ref={textareaRef}
            className="pn-markdown-editor-textarea"
            value={value}
            onChange={onTextareaInput}
            onKeyDown={onTextareaKeyDown}
            onSelect={reportActiveFormats}
            onMouseUp={reportActiveFormats}
            onKeyUp={reportActiveFormats}
            placeholder={placeholder}
            readOnly={readonly}
            spellCheck
            aria-label={placeholder}
            data-testid={tid('content')}
          />
        ) : (
          <div
            className="pn-markdown-editor-preview"
            data-testid={tid('preview')}
          >
            <Markdown>{value}</Markdown>
          </div>
        )}

        {menu ? (
          <EditorMenuPopover
            x={menu.x}
            y={menu.y}
            activeIndex={menu.index}
            items={filteredSlash}
            testIdPrefix={dataTestId}
            onSelect={selectMenuItem}
          />
        ) : null}

        {showToast ? (
          <Toast onDismiss={() => setToastDismissed(true)}>
            Couldn't save — try again.
          </Toast>
        ) : null}
      </div>
    );
  }
);
